import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CommonButton } from '@/components/ui/common-button';
import masterDataService from '@/services/masterDataService';
import productService from '@/services/productService';

const FIELD_LABELS = {
  driver_id: 'Driver',
  vehicle_id: 'Vehicle',
  rake_point_id: 'Rake Point',
  product_type_id: 'Product Type',
  product_type: 'Product Type',
  product_quantity: 'Product Quantity',
  estimated_kms: 'Estimated Kms',
  invoice_number: 'Invoice Number',
  dc_number: 'DC Number',
  eway_bill_number: 'E-way Bill Number',
  billed_to_party: 'Billed To Party',
  shipped_to_party: 'Shipped To Party',
  rr_number: 'RR Number',
  rr_date: 'RR Date',
  scheduled_start_time: 'Scheduled Start Time',
  scheduled_end_time: 'Scheduled End Time',
};

const getFieldLabel = (ch) => {
  if (ch.field_name === 'driver_id') return 'Driver';
  if (ch.field_name === 'product_type_id' || ch.field_name === 'product_type') return 'Product Type';
  return ch.display_label || FIELD_LABELS[ch.field_name] || ch.field_name?.replace(/_/g, ' ') || 'N/A';
};

const getFieldValue = (ch, driversMap = {}, productTypesMap = {}) => {
  if (!ch) return 'N/A';
  const val = ch.new_value;
  if (val === null || val === undefined || val === '') return 'N/A';

  if (ch.field_name === 'driver_id') {
    return driversMap[String(val)] || 'Loading driver...';
  }

  if (ch.field_name === 'product_type_id' || ch.field_name === 'product_type') {
    return productTypesMap[String(val)] || 'Loading product...';
  }

  if (ch.field_name === 'rr_date' || ch.field_name === 'scheduled_start_time' || ch.field_name === 'scheduled_end_time') {
    try {
      return new Date(val).toLocaleString();
    } catch {
      return String(val);
    }
  }

  return String(val);
};

const EditRequestDetailDialog = ({
  open,
  onOpenChange,
  selectedRequest,
  userProfile,
  submitting,
  activeTab,
  onCancelClick,
  onNotVerifyClick,
  onVerifyClick,
  onRejectClick,
  onApproveClick,
  driversMap: propDriversMap = {},
}) => {
  if (!selectedRequest) return null;

  const isSelfRequester = selectedRequest.requested_by === userProfile?.id;
  const userRole = userProfile?.role?.toLowerCase();
  const isSuperAdmin = ['ipl_super_admin', 'super_admin', 'superadmin', 'ipl_admin'].includes(userRole);
  const canVerify = userRole === 'port_admin' || isSuperAdmin;
  const canApprove = userRole === 'account_officer' || isSuperAdmin;

  const [localDriversMap, setLocalDriversMap] = useState({});
  const fetchedDriverIdsRef = React.useRef(new Set());

  const [localProductTypesMap, setLocalProductTypesMap] = useState({});
  const fetchedProductIdsRef = React.useRef(new Set());

  useEffect(() => {
    if (!open || !selectedRequest?.changes) return;

    const driverChanges = selectedRequest.changes.filter(
      (c) => c.field_name === 'driver_id'
    );

    driverChanges.forEach((ch) => {
      const idsToFetch = [ch.new_value, ch.old_value].filter(
        (id) => id && typeof id === 'string' && id.trim().length > 0
      );

      idsToFetch.forEach((driverId) => {
        if (fetchedDriverIdsRef.current.has(driverId) || propDriversMap[driverId]) return;
        fetchedDriverIdsRef.current.add(driverId);

        masterDataService.getDriverById(driverId)
          .then((d) => {
            const fullName = `${d?.user?.first_name || ''} ${d?.user?.last_name || ''}`.trim();
            const name = fullName || d?.user?.username || d?.name || 'Driver';
            setLocalDriversMap((prev) => ({
              ...prev,
              [String(driverId)]: name,
              ...(d?.id ? { [String(d.id)]: name } : {}),
            }));
          })
          .catch((err) => {
            console.error('Failed to load driver details:', err);
            setLocalDriversMap((prev) => ({
              ...prev,
              [String(driverId)]: 'Driver',
            }));
          });
      });
    });

    const productChanges = selectedRequest.changes.filter(
      (c) => c.field_name === 'product_type_id' || c.field_name === 'product_type'
    );

    productChanges.forEach((ch) => {
      const idsToFetch = [ch.new_value, ch.old_value].filter(
        (id) => id && typeof id === 'string' && id.trim().length > 0
      );

      idsToFetch.forEach((productId) => {
        if (fetchedProductIdsRef.current.has(productId)) return;
        fetchedProductIdsRef.current.add(productId);

        productService.getProductById(productId)
          .then((p) => {
            const name = p?.name ? (p.unit_of_measure ? `${p.name} (${p.unit_of_measure})` : p.name) : 'Product';
            setLocalProductTypesMap((prev) => ({
              ...prev,
              [String(productId)]: name,
              ...(p?.id ? { [String(p.id)]: name } : {}),
            }));
          })
          .catch((err) => {
            console.error('Failed to load product details:', err);
            setLocalProductTypesMap((prev) => ({
              ...prev,
              [String(productId)]: 'Product',
            }));
          });
      });
    });
  }, [open, selectedRequest, propDriversMap]);

  const activeDriversMap = Object.keys(propDriversMap).length > 0 ? propDriversMap : localDriversMap;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[620px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-foreground">
            Request Review & Audit
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-5">
          <div className="border border-border p-4 rounded-xl bg-card grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground block text-xs font-semibold mb-0.5">Requester</span>
              <span className="font-semibold text-foreground">{selectedRequest.requester_username}</span>
            </div>
            <div>
              <span className="text-muted-foreground block text-xs font-semibold mb-0.5">Submitted On</span>
              <span className="font-semibold text-foreground text-xs">{new Date(selectedRequest.created_at).toLocaleString()}</span>
            </div>
            <div className="col-span-1 md:col-span-2 pt-3 border-t border-border/60">
              <span className="text-muted-foreground block text-xs font-semibold mb-1">Reason for Changes</span>
              <span className="font-medium text-xs text-foreground bg-secondary/30 px-3 py-2 rounded-lg mt-0.5 block italic">
                "{selectedRequest.request_reason}"
              </span>
            </div>
          </div>

          <div className="space-y-2.5">
            <h4 className="text-xs font-bold text-muted-foreground">Changes Differences (Diff)</h4>
            <div className="border border-border rounded-xl overflow-hidden bg-card">
              <div className="overflow-auto w-full">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="h-8 text-xs font-medium">Field Label</TableHead>
                    <TableHead className="h-8 text-xs text-emerald-500 font-medium">Requested Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedRequest.changes?.map((ch, idx) => (
                    <TableRow key={ch.id || idx} className="hover:bg-transparent border-border">
                      <TableCell className="py-2.5 font-medium text-xs text-foreground">{getFieldLabel(ch)}</TableCell>
                      <TableCell className="py-2.5 text-xs text-emerald-600 font-semibold bg-emerald-500/[0.01]">
                        {getFieldValue(ch, activeDriversMap, localProductTypesMap)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </div>
          </div>

          <div className="border border-border p-4 rounded-xl bg-card/50 space-y-3 text-sm">
            <h4 className="text-xs font-bold text-muted-foreground">Workflow Audit Trail</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-muted-foreground block text-xs font-semibold mb-0.5">Verification Status</span>
                <span className="font-semibold text-foreground">
                  {selectedRequest.verification_status === 'PENDING' && 'Pending Verification'}
                  {selectedRequest.verification_status === 'VERIFIED' && 'Verified'}
                  {selectedRequest.verification_status === 'NOT_VERIFIED' && 'Not Verified'}
                </span>
              </div>
              {selectedRequest.verification_status !== 'PENDING' && (
                <div>
                  <span className="text-muted-foreground block text-xs font-semibold mb-0.5">Verified By</span>
                  <span className="font-semibold text-foreground">
                    {selectedRequest.verifier_username || 'N/A'}
                    {selectedRequest.verified_at && ` on ${new Date(selectedRequest.verified_at).toLocaleString()}`}
                  </span>
                </div>
              )}
            </div>

            {selectedRequest.verification_notes && (
              <div className="pt-2 border-t border-border/60">
                <span className="text-muted-foreground block text-xs font-semibold mb-1">Verification Notes</span>
                <span className="font-medium text-xs text-foreground bg-secondary/20 px-3 py-2 rounded-lg block italic">
                  "{selectedRequest.verification_notes}"
                </span>
              </div>
            )}

            {selectedRequest.status !== 'PENDING' && selectedRequest.status !== 'CANCELLED' && (
              <div className="pt-2 border-t border-border/60 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-muted-foreground block text-xs font-semibold mb-0.5">Final Review Status</span>
                  <span className="font-semibold text-foreground">
                    {selectedRequest.status === 'APPLIED' ? 'Applied' : 'Rejected'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground block text-xs font-semibold mb-0.5">Reviewed By</span>
                  <span className="font-semibold text-foreground">
                    {selectedRequest.reviewer_username || 'N/A'}
                    {selectedRequest.reviewed_at && ` on ${new Date(selectedRequest.reviewed_at).toLocaleString()}`}
                  </span>
                </div>
              </div>
            )}

            {selectedRequest.rejection_reason && (
              <div className="pt-2 border-t border-border/60">
                <span className="text-muted-foreground block text-xs font-semibold mb-1">Rejection Reason</span>
                <span className="font-medium text-xs text-red-600 bg-red-50 dark:bg-red-950/20 px-3 py-2 rounded-lg block italic">
                  "{selectedRequest.rejection_reason}"
                </span>
              </div>
            )}
          </div>

          {activeTab === 'PENDING' && isSelfRequester && (
            <div className="p-3 bg-amber-50 dark:bg-amber-950/10 border border-amber-200/60 dark:border-amber-900/60 rounded-xl text-xs text-amber-700 dark:text-amber-400 font-medium">
              <span className="font-semibold text-amber-800 dark:text-amber-300">Self-approval Restricted:</span> You cannot approve or verify this request because you are the original requester of these changes.
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-end gap-2">
          <CommonButton
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="w-32"
          >
            Close
          </CommonButton>

          {activeTab === 'PENDING' && (
            <>
              {isSelfRequester && (
                <CommonButton
                  variant="outline"
                  onClick={onCancelClick}
                  disabled={submitting}
                  className="w-32"
                >
                  Cancel Request
                </CommonButton>
              )}

              {canVerify && selectedRequest.verification_status === 'PENDING' && (
                <>
                  <CommonButton
                    variant="destructive"
                    onClick={onNotVerifyClick}
                    disabled={submitting}
                    className="w-32"
                  >
                    Not Verified
                  </CommonButton>

                  <CommonButton
                    variant="default"
                    onClick={onVerifyClick}
                    disabled={isSelfRequester}
                    loading={submitting}
                    className="w-32 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                  >
                    Verify Request
                  </CommonButton>
                </>
              )}

              {canApprove && selectedRequest.verification_status === 'VERIFIED' && (
                <>
                  <CommonButton
                    variant="destructive"
                    onClick={onRejectClick}
                    disabled={submitting}
                    className="w-32"
                  >
                    Reject Request
                  </CommonButton>

                  <CommonButton
                    variant="default"
                    onClick={onApproveClick}
                    disabled={isSelfRequester}
                    loading={submitting}
                    className="w-32 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                  >
                    Apply Changes
                  </CommonButton>
                </>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditRequestDetailDialog;
